import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TodoListItem } from '@unmind/design-system';
import { tracking } from 'App/Tracking';
import { useTodoListItemTracking } from '../hooks/useTodoListItemTracking';
import { useWellbeingMeasure } from '../hooks/useWellbeingMeasure';
import { WellbeingMeasureEntryPoint } from '../WellbeingMeasure/constants';
import WellbeingMeasureModal from '../WellbeingMeasure/Modal/WellbeingMeasureModal';
import { useWellbeingMeasureDeepLink } from '../WellbeingMeasure/useWellbeingMeasureDeepLink';
import { SkeletonItem } from './SkeletonItem';
import './WellbeingMeasureItem.scss';

export const WellbeingMeasureItem = () => {
  const { t: translate } = useTranslation('homepage', {
    keyPrefix: 'todo_list_section.wellbeing_measure_item',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [entryPoint, setEntryPoint] = useState(
    WellbeingMeasureEntryPoint.HomeTodo,
  );
  const [hasJustCompleted, setHasJustCompleted] = useState(false);
  const {
    isDue,
    isCompletedToday,
    hasCheckedInBefore,
    isLoading,
    questionnaire,
  } = useWellbeingMeasure({ isSurveyOpen: isModalOpen });
  const isDeepLink = useWellbeingMeasureDeepLink();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const completedTitleRef = useRef<HTMLSpanElement>(null);
  const hasHandledDeepLink = useRef(false);
  const hasTrackedDisplayed = useRef(false);

  const isCompleted = hasJustCompleted || isCompletedToday;
  const showRow = isCompleted || (isDue && Boolean(questionnaire));
  const isShownAsDue = showRow && !isCompleted;
  const copy =
    isShownAsDue && hasCheckedInBefore
      ? {
          title: translate('returning.title'),
          description: translate('returning.description'),
          cta: translate('returning.cta'),
        }
      : {
          title: translate('title'),
          description: translate('description'),
          cta: translate('cta'),
        };

  const canOpenFromDeepLink = isDeepLink && isDue && Boolean(questionnaire);

  const { trackClicked } = useTodoListItemTracking('wellbeing-measure', {
    shown: showRow,
    completed: isCompleted,
  });

  useEffect(() => {
    if (!isShownAsDue || hasTrackedDisplayed.current) {
      return;
    }

    hasTrackedDisplayed.current = true;
    tracking.track('wellbeing-measure-displayed');
  }, [isShownAsDue]);

  // Only fires once, so closing the survey doesn't reopen it.
  useEffect(() => {
    if (!canOpenFromDeepLink || hasHandledDeepLink.current) {
      return;
    }

    hasHandledDeepLink.current = true;
    setEntryPoint(WellbeingMeasureEntryPoint.DirectLink);
    setIsModalOpen(true);
  }, [canOpenFromDeepLink]);

  // The modal returns focus to the start button, which the completed row then
  // replaces, so focus would otherwise fall back to the page.
  useEffect(() => {
    if (hasJustCompleted) {
      completedTitleRef.current?.focus();
    }
  }, [hasJustCompleted]);

  // The row's gating stops once the survey is open, so a background refetch
  // can't take the survey away mid-way through.
  if (!isModalOpen) {
    if (isLoading) {
      return <SkeletonItem />;
    }

    if (!isCompleted && (!isDue || !questionnaire)) {
      return null;
    }
  }

  return (
    <>
      {showRow ? (
        <TodoListItem
          titleAsChild
          completed={isCompleted}
          className={isCompleted ? 'is-static' : undefined}
          description={copy.description}
          cta={copy.cta}
          thumbnail={{ icon: 'checkin', tone: 'secondary' }}
        >
          {isCompleted ? (
            <span ref={completedTitleRef} tabIndex={-1}>
              {translate('title')}
            </span>
          ) : (
            <button
              type="button"
              ref={buttonRef}
              onClick={() => {
                trackClicked();
                setEntryPoint(WellbeingMeasureEntryPoint.HomeTodo);
                setIsModalOpen(true);
              }}
            >
              {copy.title}
            </button>
          )}
        </TodoListItem>
      ) : null}
      {isModalOpen && questionnaire ? (
        <WellbeingMeasureModal
          questionnaire={questionnaire}
          entryPoint={entryPoint}
          returnFocusTo={buttonRef}
          onClose={() => setIsModalOpen(false)}
          onComplete={() => {
            setHasJustCompleted(true);
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
